import { useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useCmmdDeviceRegistrySnapshot } from "~/cmmd/cmmdDeviceRegistryResource";
import { useCmmdRuntimeInventory } from "~/cmmd/useCmmdRuntimeInventory";
import { useSavedEnvironmentRegistryStore } from "~/environments/runtime";
import { useRemoteThreadRouteIdentity } from "~/routes/useRemoteThreadRouteIdentity";
import { resolveThreadRouteTarget } from "~/threadRoutes";
import type { Project, SidebarThreadSummary, Thread } from "~/types";

import type { CommandPaletteActionItem } from "../CommandPalette.logic";
import {
  buildProjectContentSearchAction,
  resolveProjectContentSearchTarget,
} from "./projectContentSearchAction";
import { projectContentSearchRouteThreadRefs } from "./projectContentSearchRouteTarget";

function useProjectContentSearchPaletteTarget(input: {
  readonly activeThread: Thread | null | undefined;
  readonly projects: ReadonlyArray<Project>;
  readonly threads: ReadonlyArray<SidebarThreadSummary>;
}) {
  const routeTarget = useParams({
    strict: false,
    select: (params) => resolveThreadRouteTarget(params),
  });
  const routeThreadRef = routeTarget?.kind === "server" ? routeTarget.threadRef : null;
  const { remoteTarget, canonicalEnvironmentId } = useRemoteThreadRouteIdentity(routeThreadRef);
  const deviceRegistrySnapshot = useCmmdDeviceRegistrySnapshot();
  const runtimeInventory = useCmmdRuntimeInventory({
    registeredRuntimes: deviceRegistrySnapshot.registry?.runtimes ?? [],
    registeredDevices: deviceRegistrySnapshot.registry?.devices ?? [],
  });
  const savedEnvironmentRegistry = useSavedEnvironmentRegistryStore((state) => state.byId);

  return resolveProjectContentSearchTarget({
    activeThread: input.activeThread,
    liveProjects: input.projects,
    liveThreads: input.threads,
    replicatedProjects: runtimeInventory.sidebarProjects,
    replicatedThreads: runtimeInventory.sidebarThreads,
    routeThreadRefs: projectContentSearchRouteThreadRefs({
      routeThreadRef,
      canonicalEnvironmentId,
      remoteTarget,
    }),
    savedEnvironments: savedEnvironmentRegistry,
  });
}

export function useProjectContentSearchPaletteActions(input: {
  readonly icon: ReactNode;
  readonly activeThread: Thread | null | undefined;
  readonly projects: ReadonlyArray<Project>;
  readonly threads: ReadonlyArray<SidebarThreadSummary>;
}): CommandPaletteActionItem[] {
  const target = useProjectContentSearchPaletteTarget(input);
  const action = buildProjectContentSearchAction({ icon: input.icon, target });
  return action ? [action] : [];
}
